import { cache, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ParsedUrlQuery } from 'next-runtime/types/querystring';

import { callService } from '@api/remote-call';
import { ContentsService, HotelDetailViewModel } from '@api/v3';
import { transformCookieStore } from '@utils/SSRUtils';

export const useFetchHotelDetails = ({
  slug,
  startDate,
  endDate,
  query,
  initialData,
}: {
  slug?: string | string[];
  startDate: string;
  endDate: string;
  query: ParsedUrlQuery;
  room: string | string[] | undefined;
  initialData?: HotelDetailViewModel;
}) => {
  const [isClientSide] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (initialData) {
      queryClient.setQueryData(
        ['hotelDetail', JSON.stringify(query)],
        initialData
      );
    }
  }, [initialData, query, queryClient]);

  const fetchHotelDetails = () => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');

    const formattedRoom = query
      ? Array.isArray(query.room)
        ? query.room.map((item) => item.replaceAll('_', ','))
        : typeof query.room === 'string'
        ? [query.room.replaceAll('_', ',')]
        : ['2']
      : ['2'];

    return callService(
      { service: 'HotelsService', method: 'getHotel', version: 'v3' },
      {
        slug: String(slug),
        room: formattedRoom,
        ...(startDate && endDate
          ? {
              _in: startDate,
              out: endDate,
            }
          : {}),
        ...(basketId ? { basketId } : {}),
      }
    );
  };

  const {
    data,
    refetch,
    isError: isGetHotelDetailError,
    isInitialLoading: isLoading,
    isFetching: isHotelFetching,
    isSuccess: isGetHotelDetailSuccess,
  } = useQuery(['hotelDetail', JSON.stringify(query)], fetchHotelDetails, {
    enabled: Boolean(slug && isClientSide),
    initialData: initialData,
  });

  return {
    data,
    refetch,
    isGetHotelDetailError,
    isGetHotelDetailSuccess,
    isLoading,
    isHotelFetching,
  };
};
